// Global Instructions Rule Applied! - Apr 21, 2026
import React, { useState, useCallback, useMemo, type MouseEvent } from 'react'
import { Select, Avatar } from 'antd'
import { debounce } from 'lodash'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSearch } from '@fortawesome/free-solid-svg-icons'
import { portalColors } from '../../../portal-configs/colors'
import { getContactColor } from '../../components/UserIconList'
import { useTabManagerContext } from '../lib/TabManagerContext.tsx'
import { searchGlobal } from '../../services/utilitiesService.ts'
import { getAccessToken } from '../../auth/authService'
import { store } from '../../../state_manager/store.ts'
import {
  GLOBAL_SEARCH_BUCKET_KEYS,
  GLOBAL_SEARCH_BUCKET_LABELS,
  GLOBAL_SEARCH_DEBOUNCE_MS,
  GLOBAL_SEARCH_MAX_VISIBLE_PER_BUCKET,
  GLOBAL_SEARCH_SUBTITLE_BUCKET_KEYS,
  createEmptyGlobalSearchResults,
  hasAnyGlobalSearchResults,
  getTabModuleForSearchEntityType,
  type GlobalSearchBucketKey
} from '../../config/globalSearch.ts'
import type { GlobalSearchResult } from '../../services/utilitiesService.ts'
import Button from '../../components/Button.jsx'
import { NavbarAddButton } from './NavbarAddButton.tsx'

const getAvatarInitials = (label: string | null | undefined) => {
  if (!label) return '??'
  const parts = String(label).trim().split(' ')
  if (parts.length >= 2) return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase()
  return String(label).substring(0, 2).toUpperCase()
}

export function NavbarSearch() {
  const { openFormInTab } = useTabManagerContext()
  const [searchDropdownOpen, setSearchDropdownOpen] = useState(false)
  const [searchResults, setSearchResults] = useState(() => createEmptyGlobalSearchResults())
  const [searchLoading, setSearchLoading] = useState(false)
  const [hasSearched, setHasSearched] = useState(false)
  const [expandedBuckets, setExpandedBuckets] = useState<Partial<Record<GlobalSearchBucketKey, boolean>>>({})

  const performSearch = useCallback(async (query: string) => {
    if (!query || !query.trim()) {
      setSearchResults(createEmptyGlobalSearchResults())
      return
    }
    setSearchLoading(true)
    setHasSearched(true)
    setExpandedBuckets({})
    const token = getAccessToken(store.getState().auth)
    const res = await searchGlobal(query.trim(), token)
    setSearchLoading(false)
    if (res.success && res.data) {
      setSearchResults(res.data)
    } else {
      setSearchResults(createEmptyGlobalSearchResults())
    }
  }, [])

  const debouncedSearch = useMemo(
    () =>
      debounce((searchValue: string) => {
        if (searchValue && searchValue.length >= 2) {
          performSearch(searchValue)
        }
      }, GLOBAL_SEARCH_DEBOUNCE_MS),
    [performSearch]
  )

  const handleSearch = useCallback(
    (searchValue: string) => {
      if (!searchValue) {
        debouncedSearch.cancel()
        setSearchResults(createEmptyGlobalSearchResults())
        setHasSearched(false)
        setExpandedBuckets({})
      } else {
        debouncedSearch(searchValue)
      }
    },
    [debouncedSearch]
  )

  const hasResults = hasAnyGlobalSearchResults(searchResults)
  const firstSearchBucketKey = GLOBAL_SEARCH_BUCKET_KEYS[0]

  const handleResultClick = useCallback(
    (item: GlobalSearchResult) => {
      setSearchDropdownOpen(false)
      const module = getTabModuleForSearchEntityType(item.entity_type)
      if (module && item.entity_id) {
        openFormInTab(module, item.entity_id, item.title)
      }
    },
    [openFormInTab]
  )

  return (
    <div className='absolute left-1/2 -translate-x-1/2 flex items-center'>
      <Select
        showSearch
        filterOption={false}
        onSearch={handleSearch}
        placeholder='Search'
        size='small'
        prefix={<FontAwesomeIcon icon={faSearch} style={{ color: portalColors.neutral[400] }} />}
        onChange={() => {}}
        options={[{ label: '\u00A0', value: '_trigger' }]}
        allowClear
        open={searchDropdownOpen}
        onOpenChange={setSearchDropdownOpen}
        onFocus={() => setSearchDropdownOpen(true)}
        notFoundContent={null}
        style={{
          width: '350px',
          height: '30px',
          borderRadius: '6px'
        }}
        popupMatchSelectWidth
        styles={{ popup: { root: { borderRadius: '6px', padding: 0 } } }}
        popupRender={() => (
          <div className='bg-white rounded-b-lg shadow-lg overflow-hidden'>
            {searchLoading ? (
              <div className='py-6 px-4 text-center text-sm' style={{ color: portalColors.neutral[500] }}>
                Searching...
              </div>
            ) : hasResults ? (
              <div className='py-2'>
                {GLOBAL_SEARCH_BUCKET_KEYS.map((key) => {
                  const items = searchResults[key]
                  const sectionLabel = GLOBAL_SEARCH_BUCKET_LABELS[key]
                  if (!items || items.length === 0) return null
                  const showAll = Boolean(expandedBuckets[key])
                  const visibleItems = showAll ? items : items.slice(0, GLOBAL_SEARCH_MAX_VISIBLE_PER_BUCKET)
                  const hasMore = items.length > GLOBAL_SEARCH_MAX_VISIBLE_PER_BUCKET && !showAll
                  return (
                    <div key={key} className={key !== firstSearchBucketKey ? 'border-t border-gray-200' : ''}>
                      <div className='px-3 py-1.5'>
                        <span className='font-semibold text-sm' style={{ color: portalColors.neutral[800] }}>
                          {sectionLabel}
                        </span>
                      </div>
                      {visibleItems.map((item) => (
                        <div
                          key={`${item.entity_type}-${item.entity_id}`}
                          className={`flex gap-3 px-3 py-2 cursor-pointer hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-b-0 ${
                            GLOBAL_SEARCH_SUBTITLE_BUCKET_KEYS.has(key) && item.subtitle
                              ? 'items-start'
                              : 'items-center'
                          }`}
                          onClick={() => handleResultClick(item)}
                        >
                          <Avatar
                            size={32}
                            className={
                              GLOBAL_SEARCH_SUBTITLE_BUCKET_KEYS.has(key) && item.subtitle
                                ? 'mt-0.5 shrink-0'
                                : 'shrink-0'
                            }
                            style={{
                              backgroundColor: getContactColor(item.entity_id),
                              color: '#fff',
                              fontSize: '12px',
                              fontWeight: 600
                            }}
                          >
                            {getAvatarInitials(item.title)}
                          </Avatar>
                          <div className='flex-1 min-w-0'>
                            <div className='text-sm truncate' style={{ color: portalColors.neutral[800] }}>
                              {item.title}
                            </div>
                            {GLOBAL_SEARCH_SUBTITLE_BUCKET_KEYS.has(key) && item.subtitle ? (
                              <div
                                className='text-xs truncate mt-0.5 leading-tight'
                                style={{ color: portalColors.neutral[500] }}
                              >
                                {item.subtitle}
                              </div>
                            ) : null}
                          </div>
                        </div>
                      ))}
                      {hasMore && (
                        <div className='px-3 pb-2'>
                          <Button
                            type='link'
                            size='small'
                            className='!p-0 !h-auto'
                            onClick={(e: MouseEvent<HTMLElement>) => {
                              e.stopPropagation()
                              setExpandedBuckets((prev) => ({ ...prev, [key]: true }))
                            }}
                          >
                            View more
                          </Button>
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            ) : (
              <div className='py-6 px-4 text-center text-sm' style={{ color: portalColors.neutral[500] }}>
                {hasSearched ? 'No results found' : 'Type to search'}
              </div>
            )}
          </div>
        )}
      />
      {/* Positioned absolutely off the wrapper so the search box stays exactly centered */}
      <div className='absolute left-full top-1/2 -translate-y-1/2 ml-2'>
        <NavbarAddButton />
      </div>
    </div>
  )
}
